import React from 'react';
import { Clock, User, CalendarDays, Users } from 'lucide-react';
import { SheetBackButton } from './SheetBackButton';
import type { User as UserType } from '../../types';
import type { CartItem } from '../../services/cartService';
import { cartCatalogUnitPriceTaxIncl } from './cartCatalogDisplayPrice';
import CloverPaymentForm from './CloverPaymentForm';
import { CHECKOUT_ZERO_TEST } from '../../lib/checkoutZeroTest';
import type { CheckoutTotals } from './checkoutFormTotals';
import type { CheckoutFormFieldState } from './checkoutFormPersistReservations';
import { useLanguage } from '../../contexts/useLanguage';

type TFn = (key: string) => string;

/** Nights between stay start and end (checkout day excludes that night). */
function checkoutNightCount(start: Date, end: Date): number {
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  const ms = e.getTime() - s.getTime();
  const days = Math.round(ms / 86400000);
  return Math.max(0, days);
}

function formatCheckoutDate(d: Date, locale: string): string {
  try {
    return d.toLocaleDateString(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return d.toLocaleDateString();
  }
}

interface CheckoutStaySummarySlice {
  startDate?: Date | null;
  endDate?: Date | null;
  groups?: Array<{ id: string; adults: number; kids: number }>;
}

function CheckoutStaySummaryCard({
  t,
  shoppingCartData,
}: {
  t: TFn;
  shoppingCartData?: CheckoutStaySummarySlice;
}) {
  const { language } = useLanguage();
  const locale = language === 'fr' ? 'fr-CA' : language === 'zh' ? 'zh-CN' : 'en-CA';

  const rawStart = shoppingCartData?.startDate ?? null;
  const rawEnd = shoppingCartData?.endDate ?? null;
  if (!rawStart || !rawEnd) return null;

  const startD = rawStart instanceof Date ? rawStart : new Date(rawStart);
  const endD = rawEnd instanceof Date ? rawEnd : new Date(rawEnd);
  if (Number.isNaN(startD.getTime()) || Number.isNaN(endD.getTime())) return null;

  const nights = checkoutNightCount(startD, endD);
  const groups = shoppingCartData?.groups ?? [];
  const totalAdults = groups.reduce((sum, g) => sum + g.adults, 0);
  const totalKids = groups.reduce((sum, g) => sum + g.kids, 0);

  return (
    <div className="mb-6 rounded-xl border border-nature-200 bg-gradient-to-br from-nature-50 to-white p-4 shadow-sm ring-1 ring-nature-900/5">
      <div className="mb-3 flex items-start gap-3">
        <CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-nature-700" aria-hidden />
        <div className="min-w-0">
          <h3 className="text-lg font-semibold leading-tight text-gray-900">{t('checkout.staySummaryTitle')}</h3>
          <p className="mt-0.5 text-xs text-gray-600">{t('checkout.staySummarySubtitle')}</p>
        </div>
      </div>
      <dl className="space-y-2 border-b border-nature-100 pb-3 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="shrink-0 text-gray-600">{t('shoppingCart.startDate')}</dt>
          <dd className="text-right font-medium text-gray-900">{formatCheckoutDate(startD, locale)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="shrink-0 text-gray-600">{t('shoppingCart.endDate')}</dt>
          <dd className="text-right font-medium text-gray-900">{formatCheckoutDate(endD, locale)}</dd>
        </div>
        {nights > 0 ? (
          <div className="flex justify-between gap-4">
            <dt className="shrink-0 text-gray-600">{t('shoppingCart.duration')}</dt>
            <dd className="text-right font-medium text-nature-800">
              {t('checkout.nightsCount').replace('{n}', String(nights))}
            </dd>
          </div>
        ) : null}
      </dl>
      {groups.length > 0 ? (
        <div className="mt-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {t('shoppingCart.groupDetails')}
          </div>
          <ul className="space-y-2">
            {groups.map((g, idx) => (
              <li
                key={g.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/90 px-3 py-2 text-sm text-gray-800 ring-1 ring-gray-100"
              >
                <span className="font-medium text-gray-700">{t('checkout.groupNumber').replace('{n}', String(idx + 1))}</span>
                <span className="text-right text-gray-700">
                  {g.adults} {t('shoppingCart.adults')}
                  {g.kids > 0 ? (
                    <>
                      {' · '}
                      {g.kids} {t('shoppingCart.kids')}
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap justify-between gap-2 border-t border-dashed border-nature-200 pt-2 text-sm font-semibold text-gray-900">
            <span>{t('shoppingCart.totalGuests')}</span>
            <span className="text-right">
              {totalAdults} {t('shoppingCart.adults')}
              {totalKids > 0 ? (
                <>
                  {' · '}
                  {totalKids} {t('shoppingCart.kids')}
                </>
              ) : null}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface CheckoutFormMainContentProps {
  t: TFn;
  paymentMode: 'guest' | 'member';
  timeRemaining: number;
  isTimeLow: boolean;
  formatTime: (seconds: number) => string;
  onClose: () => void;
  selectedItems: CartItem[];
  shoppingCartData?: CheckoutStaySummarySlice;
  totals: CheckoutTotals;
  /** When false, promo code field is hidden (e.g. active VUM forfait). */
  showPromoCouponField?: boolean;
  currentUser?: UserType | null;
  isLoggedIn: boolean;
  loginData: { email: string; password: string };
  setLoginData: React.Dispatch<React.SetStateAction<{ email: string; password: string }>>;
  loginError: string;
  formData: CheckoutFormFieldState;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleLogin: (e: React.FormEvent) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleCloverPayment: (chargeResult: { chargeId: string; amount: number; status: string }) => Promise<void>;
  isProcessingPayment: boolean;
  paymentError: string | null;
  onOpenAuth?: (mode: 'signin' | 'signup') => void;
}

const CheckoutFormMainContent: React.FC<CheckoutFormMainContentProps> = ({
  t,
  paymentMode,
  timeRemaining,
  isTimeLow,
  formatTime,
  onClose,
  selectedItems,
  shoppingCartData,
  totals,
  showPromoCouponField = true,
  currentUser,
  isLoggedIn,
  loginData,
  setLoginData,
  loginError,
  formData,
  handleInputChange,
  handleLogin,
  handleSubmit,
  handleCloverPayment,
  isProcessingPayment,
  paymentError,
  onOpenAuth,
}) => {
  const memberReady = !!(currentUser || isLoggedIn);
  const canCheckoutPay = paymentMode === 'guest' || (paymentMode === 'member' && memberReady);

  return (
  <div className="h-full min-h-0 w-full bg-white border-l border-gray-200 shadow-lg overflow-hidden flex flex-col">
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      <div
        className="flex flex-shrink-0 items-center gap-2 border-b bg-gradient-to-r from-nature-50 to-white p-4 md:p-6"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))' }}
      >
        <SheetBackButton phone="inline" onClick={onClose} />
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <h2 className="truncate text-xl font-bold text-gray-900 md:text-2xl">
            {paymentMode === 'guest' ? t('common.checkoutAsGuest') : t('common.checkoutAsMember')}
          </h2>
          {timeRemaining > 0 && (
            <div
              className={`flex shrink-0 items-center space-x-2 rounded-full px-3 py-1 text-sm font-medium ${
                isTimeLow ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
              }`}
            >
              <Clock className="h-4 w-4" />
              <span>{formatTime(timeRemaining)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] p-6 pb-[max(1.25rem,env(safe-area-inset-bottom,0px))]">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            {t('common.selectedItems')} ({selectedItems.length})
          </h3>
          <div className="space-y-2">
            {selectedItems.map((item) => {
              const unitIncl = cartCatalogUnitPriceTaxIncl(item);
              return (
                <div key={item.id} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex justify-between items-start gap-3">
                    <span className="font-medium text-gray-900">{item.itemName}</span>
                    <div className="text-right shrink-0">
                      <span className="text-nature-600 font-semibold tabular-nums">
                        ${unitIncl !== null ? unitIncl.toFixed(2) : item.itemPrice.toFixed(2)}
                        {item.type === 'accommodation'
                          ? ` ${t('common.perNightPerPerson')}`
                          : item.type === 'activity'
                            ? ` ${t('common.perPerson')}`
                            : ''}
                      </span>
                      <div className="mt-0.5 text-[11px] text-gray-400">{t('common.priceTaxIncludedShort')}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <CheckoutStaySummaryCard t={t} shoppingCartData={shoppingCartData} />

        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('common.orderSummary')}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('common.subtotal')}</span>
              <span className="text-gray-900">${totals.subtotal.toFixed(2)}</span>
            </div>
            {totals.vumForfaitTaxBase > 0 && (
              <div className="flex justify-between bg-emerald-50 -mx-1 px-2 py-1 rounded-lg">
                <span className="text-emerald-700 font-medium">🎟 Forfait VUM (base fiscale)</span>
                <span className="text-emerald-800 font-semibold">${totals.vumForfaitTaxBase.toFixed(2)}</span>
              </div>
            )}
            {totals.taxeHebergement > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('common.taxLodgingLabel')}</span>
                <span className="text-gray-900">${totals.taxeHebergement.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">{t('common.taxGstLabel')}</span>
              <span className="text-gray-900">${totals.gst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('common.taxQstLabel')}</span>
              <span className="text-gray-900">${totals.qst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-300 font-semibold">
              <span className="text-gray-900">{t('common.total')}</span>
              <span className="text-nature-600">${totals.total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-500 pt-1">{t('common.checkoutSalesTaxNote')}</p>
            {totals.vumForfaitTaxBase > 0 && (
              <p className="text-xs text-gray-400 pt-1">
                * Le forfait de ${totals.vumForfaitTaxBase.toFixed(2)} a été payé à promovalidation.com. Seules les taxes applicables sont facturées ici.
              </p>
            )}
          </div>
        </div>

        {paymentMode === 'member' && !isLoggedIn && !currentUser && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <User className="w-5 h-5 mr-2 text-blue-600" />
              {t('common.login')}
            </h3>
            {onOpenAuth && (
              <button
                type="button"
                onClick={() => onOpenAuth('signin')}
                className="w-full mb-3 bg-nature-600 text-white py-2 px-3 rounded-md hover:bg-nature-700 transition-colors text-sm font-medium"
              >
                {t('signIn.title') || 'Sign in'} / {t('signUp.title') || 'Sign up'}
              </button>
            )}
            <form onSubmit={handleLogin} className="space-y-3" autoComplete="on">
              <div>
                <label htmlFor="checkout-member-email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('guestReservation.email')}
                </label>
                <input
                  id="checkout-member-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label htmlFor="checkout-member-password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('guestReservation.password')}
                </label>
                <input
                  id="checkout-member-password"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={loginData.password}
                  onChange={(e) => setLoginData((prev) => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              {loginError && <p className="text-sm text-red-600">{loginError}</p>}
              <button type="submit" className="w-full bg-nature-600 text-white py-2 rounded-md hover:bg-nature-700 transition-colors">
                {t('common.login')}
              </button>
            </form>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
          {paymentMode === 'guest' && (
            <>
              <h3 className="text-lg font-semibold text-gray-900">{t('guestReservation.personalInfo')}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="checkout-guest-firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('guestReservation.firstName')} *
                  </label>
                  <input
                    id="checkout-guest-firstName"
                    type="text"
                    name="firstName"
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                  />
                </div>
                <div>
                  <label htmlFor="checkout-guest-lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('guestReservation.lastName')} *
                  </label>
                  <input
                    id="checkout-guest-lastName"
                    type="text"
                    name="lastName"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                  />
                </div>
                <div>
                  <label htmlFor="checkout-guest-email" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('guestReservation.email')} *
                  </label>
                  <input
                    id="checkout-guest-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                  />
                </div>
                <div>
                  <label htmlFor="checkout-guest-phone" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('guestReservation.phone')} *
                  </label>
                  <input
                    id="checkout-guest-phone"
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                  />
                </div>
              </div>
            </>
          )}

          {paymentMode === 'member' && memberReady && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('guestReservation.personalInfo')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('guestReservation.firstName')}:</span>
                  <span className="font-medium text-gray-900">
                    {currentUser?.firstName || formData.firstName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('guestReservation.lastName')}:</span>
                  <span className="font-medium text-gray-900">
                    {currentUser?.lastName || formData.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('guestReservation.email')}:</span>
                  <span className="font-medium text-gray-900">
                    {currentUser?.email || formData.email}
                  </span>
                </div>
                {(currentUser?.phone || formData.phone) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('guestReservation.phone')}:</span>
                    <span className="font-medium text-gray-900">
                      {currentUser?.phone || formData.phone}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {canCheckoutPay && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('guestReservation.specialRequests')}</label>
              <textarea
                name="specialRequests"
                autoComplete="off"
                value={formData.specialRequests}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
              />
            </div>
          )}

          {showPromoCouponField && canCheckoutPay && (
              <div>
                <label htmlFor="checkout-couponCode" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('guestReservation.couponCode')}
                </label>
                <input
                  id="checkout-couponCode"
                  type="text"
                  name="couponCode"
                  autoComplete="off"
                  value={formData.couponCode}
                  onChange={handleInputChange}
                  placeholder={t('guestReservation.couponCodePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                />
              </div>
            )}

          {canCheckoutPay && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comment avez-vous entendu parler de nous ? *
              </label>
              <select
                name="hearAboutUs"
                value={formData.hearAboutUs}
                onChange={handleInputChange}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500 bg-white ${!formData.hearAboutUs ? 'border-gray-300' : 'border-nature-400'}`}
              >
                <option value="">— Sélectionner —</option>
                <option value="facebook_instagram">Facebook / Instagram</option>
                <option value="google">Google</option>
                <option value="bouche_a_oreille">Bouche-à-oreille (amis / famille)</option>
                <option value="vum">VUM / Forfait cadeau</option>
                <option value="publicite">Publicité / Flyer</option>
                <option value="deja_venu">Déjà venu(e)</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          )}

          {canCheckoutPay && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Méthode de paiement *</p>
              {CHECKOUT_ZERO_TEST && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Mode test — paiement à 0&nbsp;$. Le courriel de confirmation sera quand même envoyé.
                </div>
              )}
              <div className="mb-3 rounded-xl border-2 border-nature-500 bg-nature-50 p-4">
                <p className="font-semibold text-gray-900">Paiement au complet (100%)</p>
                <p className="mt-0.5 text-sm text-gray-500">
                  Montant total :{' '}
                  <span className="font-medium text-gray-700">${totals.total.toFixed(2)} CAD</span>
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <CloverPaymentForm
                  totalAmount={totals.total}
                  onTokenReady={() => {}}
                  onSubmit={handleCloverPayment}
                  isProcessing={isProcessingPayment}
                />
                {paymentError && <p className="mt-2 text-sm text-red-600">{paymentError}</p>}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  </div>
  );
};

export default CheckoutFormMainContent;
